import React, { useContext, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Constants from 'expo-constants';
import { Button, Overlay } from '@rneui/themed';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import CdnImageBackground from '../common/CdnImageBackground';
import { FormInput } from '../common/FormInput';
import Recaptcha from '../common/ReCaptcha';
import { StyledWebView } from '../common/StyledWebView';
import { H5, Paragraph2 } from '../text';
import AppConstants from '../../common/Constants';
import Utils from '../../common/Utils';
import useAxios from '../../hooks/useAxios';
import LayoutContext from '../../contexts/Layout';

const STYLED_WEB_VIEW_ID = 'nameAcquisition';
const STYLED_WEB_VIEW_SUBMISSION_RESPONSE_ID = `${STYLED_WEB_VIEW_ID}-submission-response`;

const FormContent = ({ layout, submit, submitButtonText, textColor }) => {
    const {
        control,
        formState: { errors, isSubmitting, isSubmitSuccessful, isDirty, isValid },
        handleSubmit,
        reset
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            firstname: '',
            lastname: '',
            email: ''
        }
    });

    useEffect(() => {
        let unmount = true;
        if (unmount && isSubmitSuccessful) {
            reset({
                keepDefaultValues: true,
                keepValues: false
            });
        }
        return () => (unmount = false);
    }, [isSubmitSuccessful, reset]);

    return (
        <View
            style={{
                alignItems: layout.device.isMobile || layout.device.isTablet ? 'center' : undefined,
                flex: 1,
                flexDirection: layout.device.isMobile || layout.device.isTablet ? 'column' : 'row',
                justifyContent: 'center',
                marginVertical: layout.styleGuide.spacer3
            }}
        >
            <Controller
                control={control}
                name='firstname'
                render={({ field, fieldState }) => {
                    return (
                        <FormInput
                            errorMessage={fieldState?.error?.message}
                            errorStyle={StyleSheet.flatten(Styles.errorText, {
                                color: textColor ?? 'red',
                                paddingTop: 5
                            })}
                            placeholder='First Name *'
                            placeholderStyle={StyleSheet.flatten(Styles.placeholderStyle, layout.styleGuide.h5)}
                            placeholderTextColor='#8897a2'
                            onBlur={field.onBlur}
                            onChangeText={text => field.onChange(text)}
                            value={field.value}
                            inputContainerStyle={Styles.inputContainerStyle}
                            inputStyle={StyleSheet.flatten(Styles.inputStyle, layout.styleGuide.h5)}
                            textAlign='center'
                            containerStyle={{
                                width: layout.device.isMobile || layout.device.isTablet ? '100%' : '25%',
                                marginRight: layout.styleGuide.spacer3
                            }}
                            tabIndex={1}
                        />
                    );
                }}
                rules={{
                    required: {
                        value: true,
                        message: 'First Name is required'
                    }
                }}
            />
            <Controller
                control={control}
                name='lastname'
                render={({ field, fieldState }) => {
                    return (
                        <FormInput
                            errorMessage={fieldState?.error?.message}
                            errorStyle={StyleSheet.flatten(Styles.errorText, {
                                color: textColor ?? 'red',
                                paddingTop: 5
                            })}
                            placeholder='Last Name *'
                            placeholderStyle={StyleSheet.flatten(Styles.placeholderStyle, layout.styleGuide.h5)}
                            placeholderTextColor='#8897a2'
                            onBlur={field.onBlur}
                            onChangeText={text => field.onChange(text)}
                            value={field.value}
                            inputContainerStyle={Styles.inputContainerStyle}
                            inputStyle={StyleSheet.flatten(Styles.inputStyle, layout.styleGuide.h5)}
                            textAlign='center'
                            containerStyle={{
                                width: layout.device.isMobile || layout.device.isTablet ? '100%' : '25%',
                                marginRight: layout.styleGuide.spacer3
                            }}
                            tabIndex={2}
                        />
                    );
                }}
                rules={{
                    required: {
                        value: true,
                        message: 'Last Name Field is required'
                    }
                }}
            />
            <Controller
                control={control}
                name='email'
                render={({ field, fieldState }) => {
                    return (
                        <FormInput
                            errorMessage={fieldState?.error?.message}
                            errorStyle={StyleSheet.flatten(Styles.errorText, {
                                color: textColor ?? 'red',
                                paddingTop: 5
                            })}
                            placeholder='Email *'
                            placeholderStyle={StyleSheet.flatten(Styles.placeholderStyle, layout.styleGuide.h5)}
                            placeholderTextColor='#8897a2'
                            onBlur={field.onBlur}
                            onChangeText={text => field.onChange(text)}
                            value={field.value}
                            inputContainerStyle={Styles.inputContainerStyle}
                            inputStyle={StyleSheet.flatten(Styles.inputStyle, layout.styleGuide.h5)}
                            textAlign='center'
                            containerStyle={{
                                width: layout.device.isMobile || layout.device.isTablet ? '100%' : '25%',
                                marginRight: layout.styleGuide.spacer3
                            }}
                            tabIndex={3}
                        />
                    );
                }}
                rules={{
                    required: {
                        value: true,
                        message: 'Email is Required'
                    },
                    pattern: {
                        value: Utils.ValidEmailRegExp,
                        message: 'Invalid Email Format'
                    }
                }}
            />
            <Button
                buttonStyle={StyleSheet.flatten(Styles.buttonStyle, { borderColor: textColor ?? '#fff' })}
                containerStyle={{
                    alignItems: 'center',
                    width: layout.device.isMobile || layout.device.isTablet ? '50%' : undefined
                }}
                disabled={errors?.length > 0 || !isDirty || !isValid}
                disabledStyle={Styles.buttonDisabledStyle}
                loading={isSubmitting}
                onPress={handleSubmit(submit)}
                style={{
                    shadowColor: 'black',
                    shadowOffset: { width: 2, height: 2 },
                    shadowOpacity: 0.35,
                    shadowRadius: 3,
                    borderRadius: 3
                }}
                title={submitButtonText || 'Submit'}
                titleStyle={StyleSheet.flatten(Styles.buttonTitleStyle, { color: textColor ?? '#fff' })}
                type='outline'
            />
        </View>
    );
};

const NameAcquisitionForm = props => {
    return (
        <View
            style={StyleSheet.flatten(Styles.formContainer, {
                backgroundColor: props.backgroundColor ?? '#153d6e',
                paddingLeft: props.layout.styleGuide.spacer2,
                paddingVertical: props.layout.styleGuide.spacer2
            })}
            dataSet={{
                spacerBottom: `StyleGuide.Spacer2(${props.layout.styleGuide.spacer2})`,
                spacerLeft: `StyleGuide.Spacer2(${props.layout.styleGuide.spacer2})`,
                spacerTop: `StyleGuide.Spacer2(${props.layout.styleGuide.spacer2})`
            }}
        >
            <StyledWebView html={props.introText} nodeId={STYLED_WEB_VIEW_ID} textProps={{ style: { color: props.textColor || '#fff' } }} width={props.width} />
            {props.children}
            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                <Paragraph2 extraStyle={{ color: props.textColor || '#8897a2' }} text='* denotes required' />
            </View>
        </View>
    );
};

const NameAcquisition = ({
    backgroundColor,
    backgroundImage,
    errorText,
    followContentLink,
    introText,
    cmsKey,
    onSuccessRedirect,
    submitButtonText,
    textColor,
    thankYouText,
    width,
    nodeTypeAlias = 'NameAcquisition'
}) => {
    const { postToApiServer } = useAxios();
    const { layout } = useContext(LayoutContext);
    const recaptchaRef = useRef();
    const [imageDimensions, setImageDimensions] = useState();
    const [requestState, setRequestState] = useState({ heading: '', body: '', show: false });

    const {
        formState: { errors },
        reset
    } = useForm();

    const submit = async data => {
        await recaptchaRef.current?.executeAsync();

        if (!errors || Object.keys(errors).length === 0) {
            const request = {
                firstName: data.firstname,
                lastName: data.lastname,
                emailAddress: data.email,
                nameAcquisitionKey: cmsKey,
                reCaptchaResponse: recaptchaRef.current?.getValue()
            };

            try {
                const response = await postToApiServer(AppConstants.REACT_APP_NAME_ACQUIRE, request, 'application/x-www-form-urlencoded');

                if (response) {
                    if (response.success) {
                        if (Array.isArray(onSuccessRedirect) && onSuccessRedirect.length > 0) {
                            await followContentLink(false, onSuccessRedirect[0], undefined /*props.references*/);
                        } else {
                            setRequestState({
                                heading: 'Thank You',
                                body: thankYouText || response.thankYouMessage,
                                show: true
                            });
                        }
                    } else {
                        setRequestState({
                            heading: 'Error',
                            body: errorText || response.errorMessage,
                            show: true
                        });
                    }
                }
            } catch (e) {
                setRequestState({
                    heading: 'Error',
                    body: errorText || e.message,
                    show: true
                });
            }

            recaptchaRef.current?.reset();
        } else {
            setRequestState({
                heading: 'Error',
                body: `Unexpected error encountered-${errors}`,
                show: true
            });
        }
    };

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            {backgroundImage.cdnImagePath?.length === 0 && (
                <View style={{ flex: 1 }}>
                    <NameAcquisitionForm backgroundColor={backgroundColor} introText={introText} layout={layout} textColor={textColor} width={width}>
                        <FormContent layout={layout} submit={submit} submitButtonText={submitButtonText} textColor={textColor} />
                    </NameAcquisitionForm>
                </View>
            )}
            {backgroundImage.cdnImagePath?.length > 0 && (
                <CdnImageBackground imageDimensions={imageDimensions} setImageDimensions={setImageDimensions} uri={backgroundImage.cdnImagePath} width={width}>
                    <NameAcquisitionForm backgroundColor={backgroundColor} introText={introText} layout={layout} textColor={textColor} width={width}>
                        <FormContent layout={layout} submit={submit} submitButtonText={submitButtonText} textColor={textColor} />
                    </NameAcquisitionForm>
                </CdnImageBackground>
            )}
            <Overlay
                backdropStyle={{ backgroundColor: 'rgba(0, 0, 0, .7) ' }}
                isVisible={requestState.show}
                overlayStyle={StyleSheet.flatten(Styles.overlayStyle, { width: layout.device.isMobile ? width : width * 0.5 })}
                style={StyleSheet.flatten(Styles.responseOverlayStyle, {
                    marginHorizontal: layout.styleGuide.horizontalMargin,
                    marginVertical: layout.styleGuide.verticalMargin
                })}
            >
                <H5 style={{ paddingVertical: 5 }} text={requestState.heading} />

                <StyledWebView html={requestState.body} nodeId={STYLED_WEB_VIEW_SUBMISSION_RESPONSE_ID} width={width} />

                {requestState.show && (
                    <Button
                        buttonStyle={{ borderColor: '#fff', borderWidth: 1 }}
                        containerStyle={{ alignItems: 'center', justifyContent: 'center' }}
                        onPress={() => {
                            reset();
                            setRequestState({
                                heading: '',
                                body: '',
                                show: false
                            });
                        }}
                        title='OK'
                        titleStyle={{ fontFamily: 'GothamBook', fontSize: 16 }}
                    />
                )}
            </Overlay>
            <Recaptcha ref={recaptchaRef} />
        </View>
    );
};

const Styles = StyleSheet.create({
    buttonDisabledStyle: {
        backgroundColor: '#7f868b',
        borderColor: '#7f868b',
        borderWidth: 1,
        opacity: 0.5
    },
    buttonStyle: {
        backgroundColor: '#a61a2b',
        borderWidth: 1,
        borderColor: '#fff'
    },
    buttonTitleStyle: {
        color: '#fff',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '16pt' : 16
    },
    errorText: {
        color: 'red',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '16pt' : 16,
        lineHeight: Platform.OS.equals('web') ? '18pt' : 18,
        marginVertical: 0,
        marginHorizontal: 0,
        textAlign: 'center'
    },
    formContainer: {
        alignContent: 'center',
        flex: 1,
        justifyContent: 'center'
    },
    inputStyle: {
        color: '#000',
        padding: 10,
        width: '25%'
    },
    inputContainerStyle: {
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: 4
    },
    overlayStyle: {
        boxShadow: '0,0,0,0',
        borderColor: '#dde4e9',
        borderWidth: 2
    },
    placeholderStyle: {
        alignItems: 'center',
        borderRadius: 4,
        color: '#8897a2',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '16pt' : 16,
        justifyContent: 'center',
        lineHeight: Platform.OS.equals('web') ? '18pt' : 18,
        marginVertical: 0,
        marginHorizontal: 0,
        textAlign: 'center'
    },
    requiredText: {
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '12pt' : 12,
        lineHeight: Platform.OS.equals('web') ? '14pt' : 14,
        textAlign: 'center'
    },
    responseOverlayStyle: {
        backgroundColor: '#fff',
        borderWidth: 0,
        justifyContent: 'center'
    }
});

NameAcquisition.propTypes = {
    backgroundImg: PropTypes.string,
    backgroundColor: PropTypes.string,
    errorText: PropTypes.string,
    introText: PropTypes.string,
    submitButtonText: PropTypes.string,
    thankYouText: PropTypes.string
};

export default NameAcquisition;
